import { Memoize } from 'typescript-memoize';
// eslint-disable-next-line @blumintinc/blumint/require-dynamic-firebase-imports
import { ref, serverTimestamp, update } from 'firebase/database';
import { ConnectionSentinel } from '../realtime/ConnectionSentinel';
import { POLLING_INTERVAL } from '../../../functions/src/realtime/session/config';
import { toSessionPath } from '../../../functions/src/types/realtimeDb/Session/path';
// eslint-disable-next-line @blumintinc/blumint/require-dynamic-firebase-imports
import { database } from '../../config/firebase-client/database';

export type SessionManagerProps = {
  userId: string;
  sessionId: string;
};

export class SessionManager {
  private pollingInterval?: NodeJS.Timeout;
  constructor(private readonly settings: SessionManagerProps) {}
  public connect() {
    this.sentinel.connect();
    this.startPolling();
  }

  @Memoize()
  private get sentinel() {
    return new ConnectionSentinel({
      realtimeRef: this.sessionRef,
      onConnect: () => {
        return this.updateStatus('online');
      },
      onDisconnect: () => {
        return {
          status: 'offline',
          lastChanged: serverTimestamp(),
        } as const;
      },
    });
  }

  @Memoize()
  private get sessionRef() {
    return ref(database, toSessionPath(this.userId, this.sessionId));
  }

  private get userId() {
    return this.settings.userId;
  }

  private get sessionId() {
    return this.settings.sessionId;
  }

  @Memoize()
  // eslint-disable-next-line @blumintinc/blumint/semantic-function-prefixes
  private async updateStatus(status: 'online' | 'offline') {
    await update(this.sessionRef, {
      status,
      lastChanged: serverTimestamp(),
    });
  }

  private startPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    // eslint-disable-next-line no-restricted-globals
    this.pollingInterval = setInterval(async () => {
      await this.updateLastChanged();
    }, POLLING_INTERVAL);
  }

  @Memoize()
  // eslint-disable-next-line @blumintinc/blumint/semantic-function-prefixes
  private async updateLastChanged() {
    await update(this.sessionRef, {
      lastChanged: serverTimestamp(),
    });
  }

  @Memoize()
  public async disconnect() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }
    await this.sentinel.disconnect();
  }
}
